import {Dialog, DialogActions, DialogContent, DialogTitle, TextField, FormControl, MenuItem, Select, InputAdornment} from '@material-ui/core';
import Button from "@components/common/button/Button";
import Checkbox from '@components/common/checkbox/checkbox.component';
import * as UserActions from '@services/user/user.actions';
import * as React from 'react';

import TranslateNew from '@translate/TranslateNew';
import ImgAvatar from '@components/common/avatar/ImgAvatar';
import { IFamilyMember } from '@app-types/websocket/family.types';
import { connect } from 'react-redux';
import { IWSUserInviteParent } from '@app-types/websocket/user.types';
import { FamilyMemberType } from '@services/family/family.constants';
import ArticleLink from '@components/common/ArticleLink';
import { displaySuccessMessage } from '@services/user/user.actions';
import { IUser } from '@app-types/api/user.types';
import { styleColors } from 'src/main.style';
import ContactsIcon from '@material-ui/icons/Contacts';
import { isContactPickerAvailable, pickContactPhoneNumbers } from '@helpers/contactEditor';
import { getInviteCountryCode, invitePhoneCountries, normalizeInviteContactPhone } from '@helpers/invitePhone';

const tn: TranslateNew = TranslateNew.getInstance();

interface IProps {
  isOpen: boolean;
  familyMember: IFamilyMember;
  onClose: () => void;
  inviteParent: (invite: IWSUserInviteParent) => void;
  displaySuccessMessage: (msg: string) => void;
  user: IUser;
}

interface IState {
  email: string;
  phone: string;
  emailError: boolean;
  phoneError: boolean;
  personalMessage: string;
  countryCode: string;
  contactAvailable: boolean;
  contactStatus: 'idle' | 'opening' | 'failed' | 'noPhone' | 'invalidPhone';
  contactPhones: string[];
  smsConsent: boolean;
}

export class InviteParentDialog extends React.Component<IProps, IState> {
  private mounted = false;
  private contactInFlight = false;
  private contactSequence = 0;

  constructor(props: IProps) {
    super(props);

    this.state = {
      email: "",
      phone: "",
      emailError: false,
      phoneError: false,
      personalMessage: "",
      countryCode: getInviteCountryCode(props.user.country),
      contactAvailable: false,
      contactStatus: 'idle',
      contactPhones: [],
      smsConsent: false,
    };
  }

  public componentDidMount() {
    this.mounted = true;
    document.addEventListener('deviceready', this.checkContactPicker);
    this.checkContactPicker();
  }

  public componentDidUpdate(previous: IProps) {
    if (previous.isOpen !== this.props.isOpen || previous.familyMember.id !== this.props.familyMember.id) {
      this.contactSequence++;
      this.setState({ contactPhones: [], contactStatus: 'idle', smsConsent: false });
      if (this.props.isOpen) { this.checkContactPicker(); }
    }
  }

  public componentWillUnmount() {
    this.mounted = false;
    this.contactSequence++;
    document.removeEventListener('deviceready', this.checkContactPicker);
  }

  public isChild() {
    return this.props.familyMember.role === FamilyMemberType.CHILD;
  }


  public render() {
    return (
      <Dialog
        open={this.props.isOpen}
        onClose={this.props.onClose}
      >

        <ArticleLink style={{ display: "flex", padding: "10px", justifyContent: "flex-end", color: "inherit", textDecoration: "none" }}
          article={tn.lang.inviteF2ReadMoreLink}>{tn.lang.readMore}
        </ArticleLink>
        <div style={{ display: 'flex', justifyContent: 'center', marginTop: '24px' }}>
          <ImgAvatar imgName={this.props.familyMember.avatar} color={this.props.familyMember.color}
            imgUrl={this.props.familyMember.imageAvatar ? this.props.familyMember.avatarURL : undefined} />
        </div>
        <DialogTitle style={{ textAlign: 'center' }}>
          {tn.lang.invite + " " + this.props.familyMember.firstName}
        </DialogTitle>
        <DialogContent style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <FormControl variant="outlined" style={{ minWidth: 85 }}>
              <Select
                value={this.state.countryCode}
                onChange={(e) => {
                  const newCountryCode = e.target.value as string;
                  this.setState({
                    countryCode: newCountryCode,
                    phoneError: !tn.testPhoneIsValid(this.state.phone, newCountryCode),
                  });
                }}
                inputProps={{
                  name: 'countryCode',
                  id: 'country-code-select',
                }}
              >
                <MenuItem value="">{tn.lang.emailOnly}</MenuItem>
                {invitePhoneCountries.map(option => (
                  <MenuItem key={option.code} value={option.code}>{option.label}</MenuItem>
                ))}
              </Select>
            </FormControl>
            <TextField
              id="phone"
              name="phone"
              variant='outlined'
              type="tel"
              style={{ flex: 1, minWidth: 0 }}
              autoComplete="tel-national"
              inputProps={{ inputMode: 'tel' }}
              label={tn.lang.labelPhone}
              onChange={(e) => {
                const phone = e.target.value.trim();
                const isValid = tn.testPhoneIsValid(phone, this.state.countryCode)
                this.setState({
                  phone,
                  phoneError: !isValid,
                  smsConsent: phone.length > 0 && this.state.smsConsent,
                });
              }}
              disabled={this.state.countryCode.length === 0}
              value={this.state.phone}
              onBlur={() => { this.setState({ phoneError: !tn.testPhoneIsValid(this.state.phone, this.state.countryCode) }) }}
              error={this.state.phoneError}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <span>{this.state.countryCode}</span>
                  </InputAdornment>
                ),
              }}
            />
          </div>
          <div style={{ fontSize: '10px', marginTop: '-17px', color: styleColors.coralRed, display: this.state.phoneError ? 'initial' : 'none' }}>
            {this.state.phoneError && tn.lang.getPhoneValidationMessage(this.state.countryCode)}
          </div>
          {this.state.phone.length > 0 && (
            <Checkbox
              id="inviteSmsConsent"
              label={tn.lang.inviteSmsConsent}
              checked={this.state.smsConsent}
              onChange={(event: React.ChangeEvent<HTMLInputElement>) => this.setState({ smsConsent: event.currentTarget.checked })}
            />
          )}
          {this.state.contactAvailable && (
            <div>
              <Button variant="outlined" color="primary" fullWidth={true}
                style={{ minHeight: 48 }}
                disabled={this.state.contactStatus === 'opening'}
                onClick={this.chooseContact}>
                <ContactsIcon fontSize="small" />
                <span>{this.state.contactStatus === 'opening' ? tn.lang.inviteOpeningContacts : tn.lang.inviteChooseContact}</span>
              </Button>
              {['failed', 'noPhone', 'invalidPhone'].includes(this.state.contactStatus) && (
                <p role="alert" style={{ color: styleColors.coralRed, fontSize: 12, marginBottom: 0 }}>
                  {this.state.contactStatus === 'noPhone' ? tn.lang.inviteContactNoPhone
                    : this.state.contactStatus === 'invalidPhone' ? tn.lang.inviteContactInvalidPhone : tn.lang.inviteContactError}
                </p>
              )}
            </div>
          )}
          {!this.isChild() && (
            <TextField
              id="email"
              name="email"
              variant='outlined'
              type="email"
              label={tn.lang.labelEmail}
              onChange={(e) => {
                const email = e.target.value.trim();
                const isValid = tn.testEmailIsValid(email);
                this.setState({
                  email,
                  emailError: !isValid,
                });
              }}
              onBlur={() => this.setState({ emailError: !tn.testEmailIsValid(this.state.email) })}
              value={this.state.email}
              error={this.state.emailError}
            />
          )}
          <div style={{ fontSize: '10px', marginTop: '-17px', color: styleColors.coralRed, display: this.state.emailError ? 'initial' : 'none' }}>
            {tn.lang.validationEmailNotValid}
          </div>

          {this.props.familyMember.role === FamilyMemberType.PARENT && (
            <TextField
              id="personalMessage"
              name="personalMessage"
              variant='outlined'
              multiline={true}
              minRows={4}
              label={tn.lang.labelMessageOptional}
              onChange={(e) => this.setState({ personalMessage: e.target.value.slice(0, 100) })}
              value={this.state.personalMessage}
              helperText={`${this.state.personalMessage.length}/100`}
            />
          )}


        </DialogContent>
        <DialogActions>
          <Button onClick={this.props.onClose}>{tn.lang.cancel}</Button>
          <Button variant="contained" color="primary"
            disabled={
              this.state.contactStatus === 'opening' || this.state.phoneError || this.state.emailError ||
              (!this.state.email && !this.state.phone) ||
              (this.state.phone.length > 0 && !this.state.smsConsent)
            }
            onClick={() => this.invite()}>
            {tn.lang.sendInvite}
          </Button>
        </DialogActions>
        <Dialog open={this.props.isOpen && this.state.contactPhones.length > 1}
          onClose={() => this.setState({ contactPhones: [] })}
          aria-labelledby="invite-contact-phone-title">
          <DialogTitle id="invite-contact-phone-title">{tn.lang.inviteChooseContactPhone}</DialogTitle>
          <DialogContent style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            {this.state.contactPhones.map(phone => (
              <Button key={phone} variant="outlined" color="primary" style={{ minHeight: 48 }}
                onClick={() => this.useContactPhone(phone)}>{phone}</Button>
            ))}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => this.setState({ contactPhones: [] })}>{tn.lang.cancel}</Button>
          </DialogActions>
        </Dialog>
      </Dialog>
    );
  }

  private checkContactPicker = async () => {
    const contactAvailable = await isContactPickerAvailable();
    if (this.mounted) { this.setState({ contactAvailable }); }
  };

  private chooseContact = async () => {
    if (!this.props.isOpen || !this.state.contactAvailable || this.contactInFlight) { return; }
    this.contactInFlight = true;
    const sequence = this.contactSequence;
    this.setState({ contactStatus: 'opening', contactPhones: [] });
    try {
      const phones = await pickContactPhoneNumbers();
      if (!this.mounted || sequence !== this.contactSequence) { return; }
      this.setState({ contactStatus: 'idle' });
      if (phones === null) { return; }
      if (!phones.length) { this.setState({ contactStatus: 'noPhone' }); }
      else if (phones.length === 1) { this.useContactPhone(phones[0]); }
      else { this.setState({ contactPhones: phones }); }
    } catch (error) {
      if (this.mounted && sequence === this.contactSequence) { this.setState({ contactStatus: 'failed' }); }
    } finally {
      this.contactInFlight = false;
    }
  };

  private useContactPhone = (value: string) => {
    const result = normalizeInviteContactPhone(value, this.state.countryCode || getInviteCountryCode(this.props.user.country));
    if (!result) {
      this.setState({ contactPhones: [], contactStatus: 'invalidPhone' });
      return;
    }
    this.setState({
      ...result,
      phoneError: !tn.testPhoneIsValid(result.phone, result.countryCode),
      contactPhones: [],
      contactStatus: 'idle',
      smsConsent: false,
    });
  };

  private invite() {
    if (this.state.email.length === 0 && this.state.phone.length === 0) {
      this.setState({ phoneError: true, emailError: true });
      return;
    }
   
    const completePhoneNumber = this.state.phone.length > 3? (this.state.countryCode + this.state.phone): undefined;

    this.props.displaySuccessMessage(tn.lang.sent);
    this.props.inviteParent({
      account: this.props.familyMember.id,
      email: (!this.isChild() && this.state.email.length > 0 ? this.state.email : undefined),
      phone: completePhoneNumber,
      personalMessage: this.state.personalMessage.length > 0 ? this.state.personalMessage : undefined
    });
    this.props.onClose();
  }
}



const mapDispatchToProps = {
  inviteParent: UserActions.inviteParent,
  displaySuccessMessage,
};

const mapStateToProps = (state: any) => ({
    user: state.user.session.data,
});



export default connect(mapStateToProps, mapDispatchToProps)(InviteParentDialog);



// WEBPACK FOOTER //
// ./src/components/extended-family/InviteParentDialog.tsx